import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTheme } from '@material-ui/core/styles';

import DetailsSection from '../DetailsSection';
import PlatformButton from '../../../../components/Button/PlatformButton';
import { platformState } from '../helper';
import { getIconColour } from '../../../../helpers/app';
import { getErrorMessage } from '../../../../helpers/formErrorMessages';
import TooltipInput from '../../../../components/Input/TooltipInput';
import { TOOLTIPS } from '../../../../constants/tooltips';
import DesktopIcon from '../../../../assets/icons/Desktop';
import { ButtonRow, ExpandContent, SectionRow } from '../styles';

import AndroidImage from '../../../../assets/Android';
import AppleImage from '../../../../assets/Apple';
import WebImage from '../../../../assets/Web';
import { ApplicationInterface } from '../../../../interfaces/application';

type PropsType = {
  formHook: UseFormReturn<ApplicationInterface>;
  expand?: boolean;
  onClick: () => void;
};

const ExpandedPlatforms: React.FC<{ formHook: UseFormReturn<ApplicationInterface> }> = ({
  formHook,
}) => {
  const {
    watch,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = formHook;

  const platforms = (watch('info.platforms') as string[]) || [];
  const validRedirectUris = (watch('setup.validRedirectUris') as string[]) || [];

  useEffect(() => {
    register('info.platforms');
    register('setup.validRedirectUris');
  }, []);

  const onPlatformClick = (platform: string) => {
    formHook.clearErrors('info.platforms');
    if (platforms.includes(platform)) {
      setValue(
        'info.platforms',
        platforms.filter((p) => p !== platform),
      );
      // @ts-ignore
      setValue(`info.appLinks.${platform.toLowerCase()}`, '');
    } else {
      setValue('info.platforms', [...platforms, platform]);
      // @ts-ignore
      setValue(`info.appLinks.${platform.toLowerCase()}`, getValues('info.website'));
    }
  };

  const onCallbackChange = (e: any) => {
    formHook.clearErrors('setup.validRedirectUris');
    setValue(`setup.validRedirectUris`, e.target.value.split(','));
  };

  return (
    <ExpandContent>
      <ButtonRow>
        <PlatformButton
          Image={WebImage}
          selected={platforms.includes('Web')}
          name="Web"
          onClick={() => onPlatformClick('Web')}
        />
        <PlatformButton
          selected={platforms.includes('Android')}
          name="Android"
          Image={AndroidImage}
          onClick={() => onPlatformClick('Android')}
        />
        <PlatformButton
          Image={AppleImage}
          selected={platforms.includes('iOS')}
          name="iOS"
          onClick={() => onPlatformClick('iOS')}
        />
      </ButtonRow>

      <SectionRow>
        <TooltipInput
          tooltipText={TOOLTIPS.OAuthRedirectUris}
          wide
          card
          id="redirects"
          label="OAuth Redirect URI(s)"
          multiline
          rows={4}
          className="wide"
          variant="outlined"
          value={validRedirectUris}
          onChange={onCallbackChange}
          error={!!errors.setup?.validRedirectUris}
          // @ts-ignore
          helperText={getErrorMessage(errors.setup?.validRedirectUris)}
          focused={false}
        />
      </SectionRow>
    </ExpandContent>
  );
};

const Platforms: React.FC<PropsType> = ({ formHook, expand, onClick }) => {
  const type = platformState(formHook);
  const theme = useTheme();

  return (
    <DetailsSection
      icon={<DesktopIcon stroke={getIconColour(type, theme)} />}
      title="Platforms"
      description="Web, iOS, and/or Android app links and authentication."
      expand={expand}
      onClick={onClick}
      type={type}
    >
      <ExpandedPlatforms formHook={formHook} />
    </DetailsSection>
  );
};

export default Platforms;
